import React, { useState, useEffect } from 'react';
import { getDatabase, ref, update } from 'firebase/database';
import './SEOSettings.css';

/**
 * SEO Settings Component für Pages
 * Ermöglicht Custom Meta-Tags, OG-Images, etc. pro Page
 */
const SEOSettings = ({ pageId, pageData, onUpdate, isGlobalModal = false, onSEOChange }) => {
  const [seoData, setSeoData] = useState({
    title: '',
    description: '',
    keywords: [],
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    ...pageData.seoSettings
  });
  const [saving, setSaving] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');

  const db = getDatabase();

  useEffect(() => {
    if (pageData.seoSettings) {
      setSeoData({
        title: '',
        description: '',
        keywords: [],
        ogTitle: '',
        ogDescription: '',
        ogImage: '',
        ...pageData.seoSettings
      });
    }
  }, [pageData.seoSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('🔍 SEOSettings: Saving with pageId:', pageId);
      const pageRef = ref(db, `/pages/${pageId}/seoSettings`);
      await update(pageRef, seoData);
      
      if (onUpdate) {
        onUpdate({ ...pageData, seoSettings: seoData });
      }
      
      console.log('✅ SEO settings saved successfully');
    } catch (error) {
      console.error('❌ Error saving SEO settings:', error);
      alert('Error saving SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    const newSeoData = {
      ...seoData,
      [field]: value
    };
    setSeoData(newSeoData);
    
    // If in GlobalModal, update parent state immediately
    if (isGlobalModal && onSEOChange) {
      onSEOChange(newSeoData);
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !seoData.keywords.includes(keywordInput.trim())) {
      const newSeoData = {
        ...seoData,
        keywords: [...seoData.keywords, keywordInput.trim()]
      };
      setSeoData(newSeoData);
      setKeywordInput('');
      
      // If in GlobalModal, update parent state immediately
      if (isGlobalModal && onSEOChange) {
        onSEOChange(newSeoData);
      }
    }
  };

  const removeKeyword = (keyword) => {
    const newSeoData = {
      ...seoData,
      keywords: seoData.keywords.filter(k => k !== keyword)
    };
    setSeoData(newSeoData);
    
    // If in GlobalModal, update parent state immediately
    if (isGlobalModal && onSEOChange) {
      onSEOChange(newSeoData);
    }
  };

  const generateAutoSEO = () => {
    const pageName = pageData.name || 'Page';
    const pageDescription = pageData.description || 'Welcome to our website';
    
    const newSeoData = {
      ...seoData,
      title: seoData.title || `${pageName} - Professional Website`,
      description: seoData.description || pageDescription,
      ogTitle: seoData.ogTitle || pageName,
      ogDescription: seoData.ogDescription || pageDescription
    };
    setSeoData(newSeoData);
    
    // If in GlobalModal, update parent state immediately
    if (isGlobalModal && onSEOChange) {
      onSEOChange(newSeoData);
    }
  };

  return (
    <div className="seo-settings">
      <div className="seo-header">
        <h3>🔍 SEO Settings</h3>
        <p>Optimize your page for search engines and social media</p>
        <button 
          className="auto-seo-btn"
          onClick={generateAutoSEO}
          type="button"
        >
          ✨ Auto-Generate SEO
        </button>
      </div>

      <div className="seo-form">
        {/* Page Title */}
        <div className="form-group">
          <label>Page Title</label>
          <input
            type="text"
            value={seoData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder={`${pageData.name || 'Page'} - Professional Website`}
            maxLength={60}
          />
          <small>{seoData.title.length}/60 characters</small>
        </div>

        {/* Meta Description */}
        <div className="form-group">
          <label>Meta Description</label>
          <textarea
            value={seoData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe your page in 1-2 sentences for search results"
            maxLength={160}
            rows={3}
          />
          <small>{seoData.description.length}/160 characters</small>
        </div>

        {/* Keywords */}
        <div className="form-group">
          <label>Keywords</label>
          <div className="keywords-input">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              placeholder="Add keyword..."
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
            />
            <button type="button" onClick={addKeyword}>Add</button>
          </div>
          <div className="keywords-list">
            {seoData.keywords.map(keyword => (
              <span key={keyword} className="keyword-tag">
                {keyword}
                <button type="button" onClick={() => removeKeyword(keyword)}>×</button>
              </span>
            ))}
          </div>
        </div>

        {/* Open Graph Settings */}
        <div className="og-settings">
          <h4>📱 Social Media Preview</h4>
          
          <div className="form-group">
            <label>OG Title (Facebook/LinkedIn)</label>
            <input
              type="text"
              value={seoData.ogTitle}
              onChange={(e) => handleInputChange('ogTitle', e.target.value)}
              placeholder={seoData.title || `${pageData.name} - Professional Website`}
              maxLength={60}
            />
          </div>

          <div className="form-group">
            <label>OG Description</label>
            <textarea
              value={seoData.ogDescription}
              onChange={(e) => handleInputChange('ogDescription', e.target.value)}
              placeholder={seoData.description || 'Describe your page for social media previews'}
              maxLength={160}
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>OG Image (1200x630px recommended)</label>
            <input
              type="url"
              value={seoData.ogImage}
              onChange={(e) => handleInputChange('ogImage', e.target.value)}
              placeholder="https://example.com/og-image.jpg"
            />
            {seoData.ogImage && (
              <div className="og-preview">
                <img src={seoData.ogImage} alt="OG Preview" />
              </div>
            )}
          </div>
        </div>

        {/* Save Button - nur wenn nicht im GlobalModal */}
        {!isGlobalModal && (
          <div className="seo-actions">
            <button 
              className="save-seo-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : '💾 Save SEO Settings'}
            </button>
          </div>
        )}

        {/* SEO Preview */}
        <div className="seo-preview">
          <h4>🔍 Search Result Preview</h4>
          <div className="search-preview">
            <div className="search-title">{seoData.title || pageData.name}</div>
            <div className="search-url">https://www.example.com{pageData.slug || '/'}</div>
            <div className="search-description">{seoData.description || pageData.description}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SEOSettings;
